import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inr, todayStr, fmtDate, CYLINDERS } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Plus, Trash2 } from "lucide-react";

export default function Invoices() {
  const { user } = useAuth();
  const canEdit = user.role === "office";
  const [category, setCategory] = useState("CYLINDER");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([{ code: "", qty: 1, amount: "" }]);
  const [arbItems, setArbItems] = useState([]);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadList = () => api.get("/hp-invoices").then((r) => setList(r.data));
  useEffect(() => { api.get("/arb/items").then((r) => setArbItems(r.data)); loadList(); }, []);
  useEffect(() => { setRows([{ code: "", qty: 1, amount: "" }]); }, [category]);

  const options = category === "CYLINDER"
    ? CYLINDERS.map((c) => ({ code: c.code, label: c.label }))
    : arbItems.map((i) => ({ code: i.id, label: `${i.name}${i.model ? " (" + i.model + ")" : ""}` }));

  const setRow = (idx, patch) => setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, { code: "", qty: 1, amount: "" }]);
  const delRow = (idx) => setRows(rows.filter((_, i) => i !== idx));
  const total = rows.reduce((a, r) => a + (parseFloat(r.amount) || 0), 0);

  const submit = async () => {
    if (!invoiceNo.trim()) { toast.error("Enter invoice number"); return; }
    const items = rows.filter((r) => r.code && parseFloat(r.amount) >= 0).map((r) => ({
      code: r.code,
      label: options.find((o) => o.code === r.code)?.label || "",
      qty: parseInt(r.qty) || 1,
      amount: parseFloat(r.amount) || 0,
    }));
    if (items.length === 0) { toast.error("Add at least one line item"); return; }
    setBusy(true);
    try {
      await api.post("/hp-invoices", { invoice_no: invoiceNo, date, category, items });
      toast.success(category === "ARB" ? "Invoice saved — ARB stock updated" : "Invoice saved");
      setInvoiceNo(""); setRows([{ code: "", qty: 1, amount: "" }]);
      api.get("/arb/items").then((r) => setArbItems(r.data));
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6 text-primary" /> HP Invoices</h1>
        <p className="text-sm text-muted-foreground">Record invoices HP raises for filled cylinders and ARB supplied to the agency. ARB invoices top up ARB stock automatically.</p>
      </div>

      {canEdit && (
        <Card className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Invoice number</Label><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} data-testid="invoice-no-input" /></div>
            <div className="flex flex-col gap-1.5"><Label>Invoice date</Label><DateField value={date} onChange={setDate} testid="invoice-date-input" className="w-full" /></div>
            <div><Label>Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="invoice-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CYLINDER">Filled cylinders</SelectItem>
                  <SelectItem value="ARB">ARB items</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Line items</Label>
            {rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6">
                  <Select value={r.code} onValueChange={(v) => setRow(idx, { code: v })}>
                    <SelectTrigger data-testid={`invoice-item-select-${idx}`}><SelectValue placeholder={category === "CYLINDER" ? "Cylinder type" : "ARB item"} /></SelectTrigger>
                    <SelectContent className="max-h-72">{options.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Input className="col-span-2" type="number" placeholder="Qty" value={r.qty} onChange={(e) => setRow(idx, { qty: e.target.value })} data-testid={`invoice-qty-${idx}`} />
                <Input className="col-span-3" type="number" placeholder="Amount ₹" value={r.amount} onChange={(e) => setRow(idx, { amount: e.target.value })} data-testid={`invoice-amount-${idx}`} />
                <Button variant="ghost" size="icon" className="col-span-1" onClick={() => delRow(idx)} disabled={rows.length === 1}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addRow} data-testid="invoice-add-row-button"><Plus className="h-4 w-4 mr-1" /> Add line</Button>
          </div>

          <div className="flex items-center justify-between border-t pt-3">
            <div className="text-sm text-muted-foreground">Invoice total</div>
            <div className="text-xl font-semibold tabular-nums">{inr(total)}</div>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full" data-testid="invoice-submit-button">Save invoice</Button>
        </Card>
      )}

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b"><h2 className="text-lg font-semibold">Recorded invoices</h2></div>
        <div className="overflow-x-auto">
          <Table data-testid="invoices-table">
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Invoice No</TableHead><TableHead>Category</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Items</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No invoices recorded yet.</TableCell></TableRow>}
              {list.map((v) => (
                <TableRow key={v.id}>
                  <TableCell>{fmtDate(v.date)}</TableCell>
                  <TableCell className="font-mono">{v.invoice_no}</TableCell>
                  <TableCell>{v.category === "ARB" ? "ARB" : "Cylinders"}</TableCell>
                  <TableCell className="text-right tabular-nums">{v.total_qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{inr(v.total_amount)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-xs truncate">{v.items.map((it) => `${it.label} ×${it.qty}`).join(", ")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{v.created_by}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
